import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import supportService from "./supportService";

const errMsg = (error) =>
  error.response?.data?.message || error.message || "Something went wrong";

const makeThunk = (type, fn) =>
  createAsyncThunk(type, async (arg, thunkAPI) => {
    try {
      return await fn(arg);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || { message: errMsg(error) });
    }
  });

// arg: { status?, silent? }  (silent = polling, loader na batavo)
export const fetchTickets = makeThunk("support/fetchAll", async (arg) => {
  const { silent, ...params } = arg || {};
  return (await supportService.getAll(params)).data;
});
// arg: { id, silent? }
export const fetchTicket = makeThunk("support/fetchOne", async ({ id }) => (await supportService.getOne(id)).data);
// arg: { id, message }
export const replyTicket = makeThunk("support/reply", async ({ id, message }) => (await supportService.reply(id, message)).data);
// arg: { id, status?, priority?, handler? }
export const updateTicket = makeThunk("support/update", async (data) => (await supportService.update(data)).data);
export const deleteTicket = makeThunk("support/delete", async (id) => {
  await supportService.remove(id);
  return id;
});

const replaceInList = (list, ticket) => list.map((t) => (t.id === ticket.id ? ticket : t));

const supportSlice = createSlice({
  name: "support",
  initialState: {
    list: [],
    active: null,      // open karelo ticket
    activeId: null,
    messages: [],      // open ticket na messages
    isLoading: false,
    isSaving: false,
    isError: false,
    message: "",
  },
  reducers: {
    // list item par click: turant batavo, pachhi fetchTicket messages lavse
    selectTicket: (s, a) => { s.active = a.payload; s.activeId = a.payload.id; s.messages = []; },
    clearActive: (s) => { s.active = null; s.activeId = null; s.messages = []; },
    clearSupportError: (s) => { s.isError = false; s.message = ""; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (s, a) => { if (!a.meta.arg?.silent) s.isLoading = true; })
      .addCase(fetchTickets.fulfilled, (s, a) => {
        s.isLoading = false;
        s.list = a.payload;
        if (s.activeId) {
          const fresh = a.payload.find((t) => t.id === s.activeId);
          if (fresh) s.active = fresh;
        }
      })
      .addCase(fetchTickets.rejected, (s, a) => {
        s.isLoading = false;
        if (!a.meta.arg?.silent) { s.isError = true; s.message = a.payload?.message || "Failed to load tickets"; }
      })

      .addCase(fetchTicket.fulfilled, (s, a) => {
        const { ticket, messages } = a.payload;
        if (s.activeId !== ticket.id) return; // user biji ticket par jati rahyo
        s.active = ticket;
        s.messages = messages;
        s.list = replaceInList(s.list, ticket);
      })

      .addCase(replyTicket.pending, (s) => { s.isSaving = true; s.isError = false; })
      .addCase(replyTicket.fulfilled, (s, a) => {
        s.isSaving = false;
        const { ticket, message } = a.payload;
        if (s.activeId === ticket.id) { s.active = ticket; s.messages.push(message); }
        s.list = replaceInList(s.list, ticket);
      })
      .addCase(replyTicket.rejected, (s, a) => {
        s.isSaving = false; s.isError = true; s.message = a.payload?.message || "Reply failed";
      })

      .addCase(updateTicket.pending, (s) => { s.isSaving = true; s.isError = false; })
      .addCase(updateTicket.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = replaceInList(s.list, a.payload);
        if (s.activeId === a.payload.id) s.active = a.payload;
      })
      .addCase(updateTicket.rejected, (s, a) => {
        s.isSaving = false; s.isError = true; s.message = a.payload?.message || "Update failed";
      })

      .addCase(deleteTicket.pending, (s) => { s.isSaving = true; })
      .addCase(deleteTicket.fulfilled, (s, a) => {
        s.isSaving = false;
        s.list = s.list.filter((t) => t.id !== a.payload);
        if (s.activeId === a.payload) { s.active = null; s.activeId = null; s.messages = []; }
      })
      .addCase(deleteTicket.rejected, (s, a) => {
        s.isSaving = false; s.isError = true; s.message = a.payload?.message || "Delete failed";
      });
  },
});

export const { selectTicket, clearActive, clearSupportError } = supportSlice.actions;
export default supportSlice.reducer;